import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X } from 'lucide-react';

/**
 * KPI項目の意義説明用ツールチップ
 *
 * 使用例:
 * <HelpTooltip title="キャッチ数とは" body={[
 *   { label: '何を測る', text: '声をかけた人の数' },
 *   { label: 'なぜ取る', text: 'ファネルの母数。少ないと全部少ない。' },
 * ]} />
 */
export default function HelpTooltip({ title, body, size = 14, color = 'var(--ink-soft)' }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        aria-label="ヘルプを表示"
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          color,
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
        }}
      >
        <HelpCircle size={size} />
      </button>

      {/* ページ切替アニメの要素が「重なりの土台」を作るため、その中に置くと z-index が
          root のボトムナビ(800)と比べられない。document.body へ出して比較させる。 */}
      {open && createPortal(
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(22,32,43,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 420,
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              background: 'var(--field)',
              border: '1px solid #33406E',
              borderRadius: 18,
              padding: 0,
              boxShadow: 'none',
            }}
          >
            {/* ヘッダー */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--rule-strong)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              gap: 10,
            }}>
              <div style={{
                fontSize: 15, fontWeight: 800,
                color: 'var(--mark)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {title}
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'var(--paper-sunk)',
                  border: 'none', borderRadius: 10,
                  width: 32, height: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--ink-soft)',
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* 本文 */}
            <div style={{ padding: '16px 20px' }}>
              {Array.isArray(body) ? body.map((item, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{
                    fontSize: 'var(--t-note)', fontWeight: 800,
                    color: item.color || 'var(--hold)',
                    marginBottom: 4,
                    letterSpacing: '0.04em',
                  }}>
                    {item.label}
                  </div>
                  <div style={{
                    fontSize: 'var(--t-note)', color: 'var(--ink)', lineHeight: 1.6,
                  }}>
                    {item.text}
                  </div>
                </div>
              )) : (
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink)', lineHeight: 1.6 }}>{body}</div>
              )}
            </div>

            {/* フッター */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--rule-strong)',
            }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  width: '100%',
                  padding: '11px',
                  background: 'var(--mark-soft)',
                  border: '1px solid #33406E',
                  borderRadius: 10,
                  color: 'var(--mark)',
                  fontSize: 'var(--t-note)', fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* ====== 各KPIのヘルプ内容 ====== */
export const HELP = {
  catch: {
    title: 'キャッチ数とは',
    body: [
      { label: '何を測る', text: '自分から声をかけた人の数' },
      { label: '⭐ なぜ取る', text: 'ファネルの一番上の数値。ここが少ないと、率が同じでも獲得は伸びない。「今日どれだけ動けたか」を可視化する指標。' },
      { label: '取らないと', text: '「今日は動けてたか」「立ち位置はどうだったか」が感覚でしか振り返れない。改善が抽象論に終わる。', color: 'var(--loss)' },
      { label: '強い人の共通点', text: '立ち位置・動きの多さ・声を出し続ける継続力・恥ずかしがらない。', color: 'var(--gain)' },
    ],
  },
  sticker: {
    title: 'ティッシュ回収組数とは',
    body: [
      { label: '何を測る', text: 'キャッチして実際にティッシュを貼ってもらえた組数 = 興味を持ってもらえた数' },
      { label: '⭐ なぜ取る', text: '「声はかけてるけど興味すら持たれてない」という弱点を発見できる。第一声の質を測る指標。' },
      { label: '取らないと', text: 'キャッチが多くてもティッシュが少ない場合に、原因が「第一声の悪さ」だと気づけない。', color: 'var(--loss)' },
      { label: '強い人の共通点', text: '第一声で自分ごと感を出す／笑顔／間／質問形でアプローチ。', color: 'var(--gain)' },
    ],
  },
  talk: {
    title: 'トーク開始数とは',
    body: [
      { label: '何を測る', text: 'キャッチトークに入れた数（足を止めて話を聞き始めてもらえた数）' },
      { label: '⭐ なぜ取る', text: '「ティッシュは貼ってもらえるけど話に入れない」段階の課題を発見できる。' },
      { label: '取らないと', text: 'ティッシュ率が高いのに獲得が伸びない理由が見えない（ティッシュ後の繋ぎが弱い等）。', color: 'var(--loss)' },
      { label: '強い人の共通点', text: 'ティッシュ後の自然な繋ぎ言葉／ベネフィット先出し／興味を引く質問。', color: 'var(--gain)' },
    ],
  },
  fulltalk: {
    title: 'フルトーク数とは',
    body: [
      { label: '何を測る', text: '着座誘導まで話し切った数（トークを最後まで展開できた数）' },
      { label: '⭐ なぜ取る', text: '「話し始めはするけど、座らせる手前で離脱される」段階の課題を発見できる。トーク開始→フルトーク→着座の繋ぎを可視化。' },
      { label: '取らないと', text: 'トークに入れても着座に繋がらない原因が「話の組み立て」なのか「誘導の弱さ」なのか切り分けられない。', color: 'var(--loss)' },
      { label: '強い人の共通点', text: '話を畳まず着座理由まで運ぶ／「座って試算しましょう」の自然な誘導／離脱前の一押し。', color: 'var(--gain)' },
    ],
  },
  seat: {
    title: '着座数とは',
    body: [
      { label: '何を測る', text: '商談スペースで座って話せた数 = 真の見込み客' },
      { label: '分業のとき', text: '自分が着座させた数＋クローザーとして渡された数。1件の受け渡しは両方の着座に数えるが、チーム全体の着座数はメモの実件数（1件）のまま。' },
      { label: '⭐ なぜ取る', text: 'ここが本番。座らせられないとクロージングまで行けない。立ち話 vs 着座で獲得率は天と地。' },
      { label: '取らないと', text: '「話してるのに座ってくれない」原因が「話の運び」なのか「座る理由」なのか特定できない。', color: 'var(--loss)' },
      { label: '強い人の共通点', text: 'ヒアリング上手／「座って試算しましょう」の自然な誘導／座る理由を明確化。', color: 'var(--gain)' },
    ],
  },
  result: {
    title: '結果（獲得/失注/後追い）とは',
    body: [
      { label: '何を測る', text: 'その着座が最終的にどうなったか（獲得・失注・後追い）' },
      { label: '⭐ なぜ取る', text: '失注理由と組み合わせて「どこで何が起きてるか」が見える。トーク改善の最重要データ。' },
      { label: '取らないと', text: '失注が累積するだけで、改善ネタにならない。同じ失注を繰り返す。', color: 'var(--loss)' },
      { label: '取った時の活用', text: '失注理由ランキング・ボトルネック診断・改善トーク準備の元データ。', color: 'var(--gain)' },
    ],
  },
  gender: {
    title: '商談相手（性別）とは',
    body: [
      { label: '何を測る', text: '商談したお客様の性別（男性・女性・両方）' },
      { label: '⭐ なぜ取る', text: '客層ごとの獲得率を分析。「男性に強い」「女性に強い」が分かると、ブース選びやトークの最適化に使える。' },
      { label: '活用例', text: '「このブースは男性客が多い」「女性向けトークを準備すべき」など、戦略の根拠になる。', color: 'var(--gain)' },
    ],
  },
  booth: {
    title: 'ブースとは',
    body: [
      { label: '何を測る', text: 'どのブースで着座したか' },
      { label: '⭐ なぜ取る', text: 'ブースごとに獲得率・失注理由が違う。「このブースは家族確認失注が多い」「ここは決定権者が来やすい」など、ブース特性が見える。' },
      { label: '活用例', text: '次にそのブースに入る前にカルテを確認 → 失敗を繰り返さない。チームで知見を共有できる。', color: 'var(--gain)' },
    ],
  },
  reflection_counters: {
    title: 'カウンター合計（キャッチ/ティッシュ回収組数/トーク開始/フルトーク）',
    body: [
      { label: '何を測る', text: '稼働中にカチカチカウンターで計測した、キャッチ数・ティッシュ回収組数・トーク開始数・フルトーク数の合計' },
      { label: '⭐ なぜ取る', text: '着座と獲得はメモから自動カウント。母数のキャッチ／ティッシュ／トーク開始／フルトークだけは「合計」で記録する必要がある。これが無いとファネル分析ができない。' },
      { label: '取らないと', text: 'ホームのファネル・獲得率・ボトルネック診断が全部「0」表示になる。「動いたのに何が悪いか分からない」状態に。', color: 'var(--loss)' },
    ],
  },
  reflection_why: {
    title: '本日の振り返りの書き方',
    body: [
      { label: '今日良かったこと', text: '今日うまくいった行動と、その理由を言語化する。再現できる勝ちパターンになる。', color: 'var(--gain)' },
      { label: '今日の課題', text: 'KPI基準・転換率を見て下がっている部分と、その原因を特定する。', color: 'var(--loss)' },
      { label: '明日改善すること', text: '明日、現場で実際に変える具体的な行動を1つ決める。' },
      { label: '🆘 困っていること', text: '自分だけで解決できない、管理者に確認・相談したいことを残す。', color: 'var(--hold)' },
    ],
  },
};
